// kanbanBoard.js
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  DndContext, 
  PointerSensor, 
  KeyboardSensor,
  useSensor, 
  useSensors,
  closestCorners,
  DragOverlay,
  defaultDropAnimationSideEffects
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Button, Space, message, notification, Typography, Divider } from 'antd';
import { 
  DragOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons';
import { Column, TaskModal, TaskDetailsModal } from '../components/kanban';
import ColumnsGrid from '../components/kanban/components/ColumnsGrid';
import EmptyState from '../components/kanban/components/EmptyState';
import BoardLayout from '../components/kanban/components/BoardLayout';
import { INITIAL_TASKS, BOARD_COLUMNS, DEFAULT_USERS } from '../components/kanban/constants';
import { readJson, writeJson, withNamespace } from '../utils/storage';

const { Title, Text } = Typography;

const STORAGE_NS = 'farscada';
const TASKS_KEY = withNamespace(STORAGE_NS, 'tasks');
const initialTasks = readJson(TASKS_KEY, INITIAL_TASKS);
const columns = BOARD_COLUMNS;
const users = DEFAULT_USERS;

const KanbanBoard = () => {
  const [tasks, setTasks] = useState(initialTasks);

  useEffect(() => {
    writeJson(TASKS_KEY, tasks);
  }, [tasks]);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);


  // Enhanced sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Drop animation configuration
  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.8',
        },
      },
    }),
  };

  // Filter tasks based on search term and filters
  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    
    if (searchTerm) {
      filtered = filtered.filter(task => 
        task.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.assignee.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.labels.some(label => label.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (filterAssignee) {
      filtered = filtered.filter(task => task.assignee === filterAssignee);
    }
    
    if (filterPriority) {
      filtered = filtered.filter(task => task.priority === filterPriority);
    }
    
    return filtered;
  }, [tasks, searchTerm, filterAssignee, filterPriority]);

  // Calculate board statistics
  const boardStats = useMemo(() => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(task => task.column === 'done').length;
    const inProgressTasks = tasks.filter(task => task.column === 'in-progress').length;
    const highPriorityTasks = tasks.filter(task => task.priority === 'High' || task.priority === 'Critical').length;
    const overdueTasks = tasks.filter(task => {
      if (task.column === 'done') return false;
      return new Date(task.endDate) < new Date();
    }).length;

    return {
      totalTasks,
      completedTasks,
      inProgressTasks,
      highPriorityTasks,
      overdueTasks,
      completionRate: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0
    };
  }, [tasks]);

  // Update column counts
  const updatedColumns = useMemo(() => {
    return columns.map(column => ({
      ...column,
      count: filteredTasks.filter(task => task.column === column.id).length
    }));
  }, [filteredTasks]);

  const handleDragStart = useCallback((event) => {
    const { active } = event;
    const task = tasks.find(t => t.id === active.id);
    setActiveTask(task);
    
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }, [tasks]);

  const handleDragOver = useCallback((event) => {
    const { active, over } = event;
    
    if (!over) return;

    let overColumnId = over.id;
    
    const overTask = tasks.find(task => task.id === over.id);
    if (overTask) {
      overColumnId = overTask.column;
    }

    updatedColumns.forEach(column => {
      const columnElement = document.getElementById(`column-${column.id}`);
      if (columnElement) {
        if (column.id === overColumnId) {
          columnElement.style.background = 'linear-gradient(135deg, #e6f7ff 0%, #f0f9ff 100%)';
          columnElement.style.border = '2px dashed #1890ff';
        } else {
          columnElement.style.background = 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)';
          columnElement.style.border = '1px solid #f0f0f0';
        }
      }
    });
  }, [tasks, updatedColumns]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    document.body.style.cursor = 'default';
    document.body.style.userSelect = 'auto';
    setActiveTask(null);

    updatedColumns.forEach(column => {
      const columnElement = document.getElementById(`column-${column.id}`);
      if (columnElement) {
        columnElement.style.background = 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)';
        columnElement.style.border = '1px solid #f0f0f0';
      }
    });

    if (!over) {
      message.warning({
        content: 'Task was not dropped in a valid area',
        duration: 2,
        style: { marginTop: '50px' },
      });
      return;
    }

    const activeTask = tasks.find(task => task.id === active.id);
    if (!activeTask) return;

    let targetColumnId;
    
    if (updatedColumns.some(col => col.id === over.id)) {
      targetColumnId = over.id;
    } else {
      const overTask = tasks.find(task => task.id === over.id);
      if (overTask) {
        targetColumnId = overTask.column;
      } else {
        message.warning('Could not determine target column');
        return;
      }
    }

    if (activeTask.column !== targetColumnId) {
      const updatedTasks = tasks.map(task => 
        task.id === active.id 
          ? { 
              ...task, 
              column: targetColumnId,
              updatedAt: new Date().toISOString().split('T')[0]
            }
          : task
      );
      
      setTasks(updatedTasks);
      
      const columnTitle = updatedColumns.find(col => col.id === targetColumnId)?.title;
      notification.success({
        message: 'Task Moved Successfully',
        description: `"${activeTask.content}" moved to ${columnTitle}`,
        placement: 'bottomRight',
        duration: 3,
      });
    } else {
      setTasks((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        
        if (oldIndex !== newIndex) {
          const newItems = arrayMove(items, oldIndex, newIndex);
          return newItems.map((item, index) => 
            index === newIndex ? { ...item, updatedAt: new Date().toISOString().split('T')[0] } : item
          );
        }
        return items;
      });
    }
  }, [tasks, updatedColumns]);

  const handleDragCancel = useCallback(() => {
    document.body.style.cursor = 'default';
    document.body.style.userSelect = 'auto';
    setActiveTask(null);

    updatedColumns.forEach(column => {
      const columnElement = document.getElementById(`column-${column.id}`);
      if (columnElement) {
        columnElement.style.background = 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)';
        columnElement.style.border = '1px solid #f0f0f0';
      }
    });
  }, [updatedColumns]);

  const handleAddTask = () => {
    setEditingTask(null);
    setIsTaskModalOpen(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setIsTaskModalOpen(true);
  };

  const handleViewDetails = (task) => {
    setViewingTask(task);
    setIsDetailsModalOpen(true);
  };

  const handleDeleteTask = (taskId) => {
    const taskToDelete = tasks.find(task => task.id === taskId);
    setTasks(tasks.filter(task => task.id !== taskId));
    
    message.success({
      content: `Task "${taskToDelete.content}" has been deleted`,
      duration: 3,
    });
  };

  const handleToggleDone = (taskId) => {
    setTasks(prev => prev.map(t =>
      t.id === taskId
        ? {
          ...t,
          column: t.column === 'done' ? 'todo' : 'done',
          updatedAt: new Date().toISOString().split('T')[0]
        }
        : t
    ));
  };
  
  const handleTaskModalSubmit = (taskData) => {
    if (editingTask) {
      setTasks(tasks.map(task => 
        task.id === editingTask.id ? { 
          ...task, 
          ...taskData,
          updatedAt: new Date().toISOString().split('T')[0]
        } : task
      ));
      message.success('Task updated successfully!');
    } else {
      const newTask = { 
        id: Date.now().toString(), 
        ...taskData, 
        column: 'todo', 
        comments: [],
        attachments: [],
        subtasks: [],
        createdAt: new Date().toISOString().split('T')[0],
        updatedAt: new Date().toISOString().split('T')[0]
      };
      setTasks([...tasks, newTask]);
      message.success('New task created successfully!');
    }
    setIsTaskModalOpen(false);
    setEditingTask(null);
  };

  const handleTaskModalCancel = () => {
    setIsTaskModalOpen(false);
    setEditingTask(null);
  };

  const handleDetailsModalClose = () => {
    setIsDetailsModalOpen(false);
    setViewingTask(null);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilterAssignee('');
    setFilterPriority('');
  };

  const handleExportData = () => {
    const dataStr = JSON.stringify(tasks, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'kanban-board-data.json';
    link.click();
    URL.revokeObjectURL(url);
    
    message.success('Data exported successfully!');
  };

  return (
    <BoardLayout
      sidebarCollapsed={sidebarCollapsed}
      setSidebarCollapsed={setSidebarCollapsed}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      filterAssignee={filterAssignee}
      setFilterAssignee={setFilterAssignee}
      filterPriority={filterPriority}
      setFilterPriority={setFilterPriority}
      boardStats={boardStats}
      filteredTasks={filteredTasks}
      tasks={tasks}
      handleClearSearch={handleClearSearch}
      handleAddTask={handleAddTask}
      handleExportData={handleExportData}
      users={users}
    >
        <ColumnsGrid
          sensors={sensors}
          updatedColumns={updatedColumns}
          filteredTasks={filteredTasks}
                activeTask={activeTask}
                searchTerm={searchTerm}
          handlers={{
            onDragStart: handleDragStart,
            onDragOver: handleDragOver,
            onDragEnd: handleDragEnd,
            onDragCancel: handleDragCancel,
            onViewDetails: handleViewDetails,
            onToggleDone: handleToggleDone,
          }}
        />

        {/* وضعیت خالی */}
        {filteredTasks.length === 0 && (
          <EmptyState
            hasFilters={Boolean(searchTerm || filterAssignee || filterPriority)}
            onClear={handleClearSearch}
            onCreate={handleAddTask}
          />
        )}

        {/* مودال‌ها */}
        <TaskModal
          visible={isTaskModalOpen}
          onCancel={handleTaskModalCancel}
          onSubmit={handleTaskModalSubmit}
          editingTask={editingTask}
          users={users}
        />

        <TaskDetailsModal
          visible={isDetailsModalOpen}
          task={viewingTask}
          onClose={handleDetailsModalClose}
          onEdit={handleEditTask}
          onDelete={handleDeleteTask}
        />
    </BoardLayout>
  );
};

export default KanbanBoard;